import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Building2,
  Calendar as CalendarIcon,
  Layers,
  Bell,
  Wand2,
} from "lucide-react";

interface MultiServiceSchedulerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Kept for backwards compatibility with existing callers, currently unused.
  onConfirm?: (payload: unknown) => void;
}

/**
 * "Plan Multiple Services" — Coming Soon teaser.
 *
 * The previous implementation tried to schedule multi-service flows but was
 * never functional. We now show a marketing-friendly preview that explains
 * what's coming for apartment / property managers and lets them register
 * interest. When the real planner ships, swap this component back to the
 * scheduling logic.
 */
export default function MultiServiceScheduler({ open, onOpenChange }: MultiServiceSchedulerProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl p-0 overflow-hidden border-0 bg-transparent shadow-2xl">
        {/* Decorative gradient frame */}
        <div className="relative rounded-xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-[1.5px]">
          <div className="relative rounded-[10px] bg-white">
            {/* Animated blob backdrop */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[10px]">
              <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-gradient-to-br from-indigo-200 to-purple-200 blur-3xl opacity-60 animate-pulse" />
              <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-gradient-to-br from-pink-200 to-amber-200 blur-3xl opacity-50 animate-pulse [animation-delay:1.5s]" />
            </div>

            <DialogHeader className="relative px-6 pt-6">
              <div className="flex items-center justify-center mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-sm">
                  <Sparkles className="h-3 w-3" />
                  Coming Soon
                </span>
              </div>
              <DialogTitle className="text-center text-2xl md:text-3xl font-bold bg-gradient-to-br from-indigo-700 via-purple-700 to-pink-600 bg-clip-text text-transparent">
                Un'unica regia per ogni servizio del tuo appartamento
              </DialogTitle>
            </DialogHeader>

            <div className="relative px-6 py-5 space-y-6">
              <p className="text-center text-sm md:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
                Stiamo preparando uno strumento dedicato agli{" "}
                <span className="font-semibold text-slate-900">apartment manager</span>{" "}
                e ai property manager per gestire <em>tutti</em> i servizi dei tuoi
                immobili — pulizie, manutenzione, giardinaggio, lavanderia — in un
                unico flusso, con un solo carrello e un solo calendario.
              </p>

              {/* Feature grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FeatureCard
                  icon={<Building2 className="h-5 w-5" />}
                  title="Multi-immobile"
                  description="Gestisci più appartamenti dallo stesso pannello, ognuno con la sua agenda."
                  gradient="from-indigo-500/10 to-indigo-500/5"
                  iconBg="bg-indigo-100 text-indigo-700"
                />
                <FeatureCard
                  icon={<Layers className="h-5 w-5" />}
                  title="Bundle di servizi"
                  description="Combina più servizi in un'unica prenotazione ricorrente con prezzo trasparente."
                  gradient="from-purple-500/10 to-purple-500/5"
                  iconBg="bg-purple-100 text-purple-700"
                />
                <FeatureCard
                  icon={<CalendarIcon className="h-5 w-5" />}
                  title="Programmazione intelligente"
                  description="Cadenze automatiche (settimanale, mensile, post check-out) sincronizzate col tuo PMS."
                  gradient="from-pink-500/10 to-pink-500/5"
                  iconBg="bg-pink-100 text-pink-700"
                />
                <FeatureCard
                  icon={<Bell className="h-5 w-5" />}
                  title="Un solo punto di contatto"
                  description="Una sola fattura mensile, un solo dashboard, un solo team a cui scrivere."
                  gradient="from-amber-500/10 to-amber-500/5"
                  iconBg="bg-amber-100 text-amber-700"
                />
              </div>

              {/* Notify-me CTA */}
              <div className="relative rounded-lg border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-purple-50/80 p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-white p-2 shadow-sm">
                    <Wand2 className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900">
                      Vuoi essere tra i primi a provarlo?
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Apriremo l'accesso anticipato agli apartment manager iscritti
                      alla nostra newsletter — scrivici a{" "}
                      <a
                        href="mailto:alessandro@halomora.com?subject=Multi-Service%20early%20access"
                        className="font-medium text-indigo-700 underline-offset-2 hover:underline"
                      >
                        alessandro@halomora.com
                      </a>{" "}
                      con oggetto "Multi-Service early access".
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  className="border-slate-300"
                  onClick={() => onOpenChange(false)}
                >
                  Torna ai servizi
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  gradient,
  iconBg,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  iconBg: string;
}) {
  return (
    <div className={`group relative rounded-lg border border-slate-200/80 bg-gradient-to-br ${gradient} p-3 transition-all hover:shadow-md hover:-translate-y-0.5`}>
      <div className="flex items-start gap-3">
        <div className={`${iconBg} rounded-md p-2 shadow-sm`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-600 mt-0.5 leading-snug">{description}</div>
        </div>
      </div>
    </div>
  );
}
